import { Link } from '@tanstack/react-router';
import { FilePlus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

import { CustomerPrivacyRequestDialog } from './customer-privacy-request-dialog';

/**
 * The shopper's opt-out state, a link to the privacy requests list filtered to them, and a way to
 * record a request the shopper made by phone or email.
 */
export function CustomerPrivacyCard({
  storeId,
  customer,
}: {
  storeId: string;
  customer: Customer;
}) {
  const { t } = useTranslation('customers');
  const [opening, setOpening] = useState(false);
  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('privacy.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3 text-sm">
        <p className={customer.doNotSell ? undefined : 'text-muted-foreground'}>
          {customer.doNotSell ? t('privacy.optedOut') : t('privacy.notOptedOut')}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link
            to="/s/$storeId/privacy-requests"
            params={{ storeId }}
            search={{ customerId: customer.id }}
          >
            <ShieldCheck aria-hidden />
            {t('privacy.link')}
          </Link>
        </Button>
        <Can permission="customers.write">
          <Button variant="outline" size="sm" onClick={() => setOpening(true)}>
            <FilePlus aria-hidden />
            {t('privacy.open')}
          </Button>
        </Can>
      </CardContent>
      {opening ? (
        <CustomerPrivacyRequestDialog
          storeId={storeId}
          customer={customer}
          open
          onOpenChange={setOpening}
        />
      ) : null}
    </Card>
  );
}
